import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Download, Lock, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isImageFile(filename) {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
}

function PrivateBadge({ isOwn, isTeacherOrAdmin, recipientName }) {
  return (
    <div className={`flex items-center gap-1 text-[10px] font-medium mb-1 px-2 py-0.5 rounded-full ${isOwn ? 'bg-amber-100 text-amber-700' : 'bg-amber-50 text-amber-600'}`}>
      <Lock className="w-2.5 h-2.5" />
      <span>{isTeacherOrAdmin && recipientName ? `Solo para: ${recipientName}` : 'Solo para ti'}</span>
    </div>
  );
}

function CommentAttachment({ file, isOwn }) {
  if (isImageFile(file.filename)) {
    return <img src={`${API}/files/${file.filename}`} alt={file.original_name} className="max-w-xs rounded-lg" />;
  }
  return (
    <a
      href={`${API}/files/${file.filename}`}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-1 text-sm ${isOwn ? 'text-blue-100 hover:text-white' : 'text-blue-600'}`}
    >
      <Download className="w-3 h-3" />{file.original_name}
    </a>
  );
}

export default function CommentItem({ comment, currentUserId, isTeacherOrAdmin, recipientName, onDelete, tTeacher }) {
  const isOwn = comment.user_id === currentUserId;
  const isTeacherComment = comment.user_role === 'teacher' || comment.user_role === 'admin';
  const initials = `${(comment.user_name || '')[0] || ''}`.toUpperCase();

  return (
    <div className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`} data-testid={`comment-${comment.id}`}>
      <Avatar className="h-8 w-8 flex-shrink-0">
        {comment.user_image && <AvatarImage src={`${API}/files/${comment.user_image}`} />}
        <AvatarFallback className={`text-xs font-bold ${isTeacherComment ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-[70%]`}>
        {comment.is_private && (
          <PrivateBadge isOwn={isOwn} isTeacherOrAdmin={isTeacherOrAdmin} recipientName={recipientName} />
        )}
        <div className={`rounded-2xl px-4 py-2.5 ${isOwn ? 'bg-blue-500 text-white rounded-tr-md' : 'bg-white border rounded-tl-md'} ${comment.is_private ? 'ring-1 ring-amber-300' : ''}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-bold ${isOwn ? 'text-blue-100' : 'text-slate-500'}`}>{comment.user_name}</span>
            {isTeacherComment && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isOwn ? 'bg-blue-400 text-white' : 'bg-blue-100 text-blue-600'}`}>
                {tTeacher}
              </span>
            )}
            {comment.is_private && <Lock className={`w-3 h-3 ${isOwn ? 'text-blue-200' : 'text-amber-400'}`} />}
          </div>
          {comment.text && <p className="text-sm whitespace-pre-wrap">{comment.text}</p>}
          {comment.files?.map((f, i) => (
            <div key={f.filename || `cf-${i}`} className="mt-2">
              <CommentAttachment file={f} isOwn={isOwn} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-1 px-1">
          <span className="text-[10px] text-slate-400">{new Date(comment.created_at).toLocaleString()}</span>
          {(isOwn || isTeacherOrAdmin) && (
            <button onClick={() => onDelete(comment.id)} className="text-slate-300 hover:text-red-400" data-testid={`delete-comment-${comment.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
